import React, { Component } from 'react';
import ReactDOM from 'react-dom';
import { parseReactProps } from '~/components/legacy/reactProps.js';

const POLL_INTERVAL_MS = 1000;
const MAX_POLLS = 1800;

class BatchLoudnessMatchingUpload extends Component {
  constructor(props) {
    super(props);
    const options = this.batchOptions();
    this.state = {
      files: [],
      preset: options.presets[0] ? options.presets[0].key : 'podcast_speech',
      items: [],
      status: 'idle',
      error: null
    };
    this.fileInputRef = React.createRef();
    this.cancelled = false;
    this.handleFileChange = this.handleFileChange.bind(this);
    this.handlePresetChange = this.handlePresetChange.bind(this);
    this.handleSubmit = this.handleSubmit.bind(this);
  }

  componentWillUnmount() {
    this.cancelled = true;
  }

  batchOptions() {
    return this.props.batchLoudnessMatching || { maxFiles: 10, presets: [] };
  }

  copy(key, fallback) {
    return (this.batchOptions().copy || {})[key] || fallback;
  }

  handleFileChange(event) {
    const files = Array.from(event.target.files || []);
    const maxFiles = this.batchOptions().maxFiles || 10;
    const tooMany = files.length > maxFiles;
    this.setState({
      files: files.slice(0, maxFiles),
      items: [],
      status: 'idle',
      error: tooMany ? this.copy('tooManyFiles', `You can process at most ${maxFiles} files at once.`).replace('%{count}', maxFiles) : null
    });
  }

  handlePresetChange(event) {
    this.setState({ preset: event.target.value, error: null });
  }

  handleSubmit(event) {
    event.preventDefault();
    if (this.state.error) return;
    if (this.state.files.length === 0) {
      this.setState({ error: this.copy('noFiles', 'Please choose at least one audio file.') }, () => {
        if (this.fileInputRef.current) this.fileInputRef.current.focus();
      });
      return;
    }

    const maxFileSize = this.props.maxFileSizeBytes;
    if (this.state.files.some((file) => maxFileSize && file.size > maxFileSize)) {
      this.setState({ error: this.copy('fileSizeExceeded', 'One of the uploaded files is too large.') }, () => {
        if (this.fileInputRef.current) this.fileInputRef.current.focus();
      });
      return;
    }

    this.cancelled = false;
    const items = this.state.files.map((file) => ({ file, name: file.name, status: 'queued', progress: 0, job: null, error: null }));
    this.setState({ items, status: 'processing', error: null }, () => this.processNext(0));
  }

  processNext(index) {
    if (this.cancelled) return;
    if (index >= this.state.items.length) {
      const failed = this.state.items.some((item) => item.status === 'failed');
      this.setState({ status: failed ? 'partial' : 'success' });
      return;
    }

    this.updateItem(index, { status: 'uploading', progress: 0 });
    this.uploadFile(this.state.items[index].file, index)
      .then((job) => {
        if (this.cancelled) return null;
        this.updateItem(index, { status: 'processing', progress: 100, job });
        return this.waitForJob(job.publicId, index);
      })
      .then(() => {
        if (!this.cancelled) this.processNext(index + 1);
      })
      .catch((error) => {
        if (this.cancelled) return;
        this.updateItem(index, { status: 'failed', error: error.message || this.copy('genericError', 'The file could not be processed.') }, () => this.processNext(index + 1));
      });
  }

  uploadFile(file, index) {
    return new Promise((resolve, reject) => {
      const xhr = new XMLHttpRequest();
      const formData = new FormData();
      formData.append('tool_key', 'batch_loudness_matching');
      formData.append('file', file, file.name);
      formData.append('configuration[preset]', this.state.preset);
      xhr.open('POST', this.props.uploadUrl, true);
      xhr.setRequestHeader('Accept', 'application/json');
      if (this.props.csrfToken) xhr.setRequestHeader('X-CSRF-Token', this.props.csrfToken);
      if (xhr.upload) {
        xhr.upload.addEventListener('progress', (event) => {
          if (event.lengthComputable) this.updateItem(index, { progress: Math.round((event.loaded / event.total) * 100) });
        });
      }
      xhr.addEventListener('load', () => {
        const payload = this.parseResponse(xhr);
        if (xhr.status >= 200 && xhr.status < 300 && payload.job) {
          if (payload.job.checkoutRequired) {
            reject(new Error(this.copy('premiumRequired', 'This premium workflow requires an eligible subscription.')));
          } else {
            resolve(payload.job);
          }
        } else {
          reject(new Error(payload.message || this.copy('genericError', 'The file could not be processed.')));
        }
      });
      xhr.addEventListener('error', () => reject(new Error(this.copy('genericError', 'The file could not be processed.'))));
      xhr.send(formData);
    });
  }

  waitForJob(publicId, index, pollCount = 0) {
    if (this.cancelled) return Promise.resolve();
    if (pollCount >= MAX_POLLS) return Promise.reject(new Error(this.copy('processingTimeout', 'Processing took too long. Please try again.')));
    const statusUrl = (this.props.statusUrlTemplate || '').replace('__PUBLIC_ID__', encodeURIComponent(publicId));
    return new Promise((resolve, reject) => {
      const xhr = new XMLHttpRequest();
      xhr.open('GET', statusUrl, true);
      xhr.setRequestHeader('Accept', 'application/json');
      xhr.addEventListener('load', () => {
        const payload = this.parseResponse(xhr);
        if (xhr.status < 200 || xhr.status >= 300 || !payload.job) {
          reject(new Error(payload.message || this.copy('genericError', 'The file could not be processed.')));
          return;
        }
        const job = payload.job;
        if (job.status === 'completed' && job.downloadUrl) {
          this.updateItem(index, { status: 'ready', job, progress: 100 }, () => resolve(job));
        } else if (job.status === 'failed' || job.status === 'expired') {
          reject(new Error(job.errorMessage || this.copy('genericError', 'The file could not be processed.')));
        } else {
          window.setTimeout(() => this.waitForJob(publicId, index, pollCount + 1).then(resolve).catch(reject), POLL_INTERVAL_MS);
        }
      });
      xhr.addEventListener('error', () => reject(new Error(this.copy('genericError', 'The file could not be processed.'))));
      xhr.send();
    });
  }

  updateItem(index, changes, callback) {
    this.setState((state) => ({ items: state.items.map((item, itemIndex) => itemIndex === index ? { ...item, ...changes } : item) }), callback);
  }

  parseResponse(xhr) {
    try {
      return JSON.parse(xhr.responseText || '{}');
    } catch (error) {
      return {};
    }
  }

  statusLabel(status) {
    const fallback = { queued: 'Waiting to be processed', uploading: 'Uploading files …', processing: 'Processing file …', ready: 'Ready', failed: 'Failed' }[status] || status;
    return this.copy(status, fallback);
  }

  render() {
    const options = this.batchOptions();
    const files = this.state.files;
    const busy = this.state.status === 'processing';
    const copy = options.copy || {};
    const uploadRequirements = [copy.maxFiles, copy.allowedFormats, copy.maxFileSize, copy.maxDuration].filter(Boolean).join(' · ');
    return (
      <form className='audio-tools-upload' onSubmit={this.handleSubmit}>
        <div className='panel panel--shadow'>
          <div className='panel__head'>
            <h2 className='text-like-h3'>{copy.uploadPanelHeading || 'Prepare the files for matching'}</h2>
            <p className='margin-bottom0'>{copy.uploadPanelHint || 'Choose your files and the intended use.'}</p>
          </div>
          <div className='panel__body'>
            <div className='grid grid--2-col grid--1-col-md-down grid--slim audio-tools-upload__fields'>
              <div className='input file optional'>
                <label htmlFor='audio-tools-batch-files'>{copy.fileLabel || 'Audio files'}</label>
                <input id='audio-tools-batch-files' ref={this.fileInputRef} type='file' multiple accept={(this.props.acceptedFormats || []).join(',')} onChange={this.handleFileChange} disabled={busy} aria-describedby={this.state.error ? 'audio-tools-batch-files-hint audio-tools-batch-error' : 'audio-tools-batch-files-hint'} aria-errormessage={this.state.error ? 'audio-tools-batch-error' : undefined} aria-invalid={this.state.error ? 'true' : undefined} />
                <p id='audio-tools-batch-files-hint' className='text-size-small text-color-secondary'>{copy.chooseFiles || 'Choose audio files'}{uploadRequirements && ` · ${uploadRequirements}`}</p>
              </div>
              <div className='input select optional'>
                <label htmlFor='audio-tools-batch-preset'>{copy.presetLabel || 'Intended use'}</label>
                <select id='audio-tools-batch-preset' value={this.state.preset} onChange={this.handlePresetChange} disabled={busy}>
                  {(options.presets || []).map((preset) => <option key={preset.key} value={preset.key}>{preset.label}</option>)}
                </select>
              </div>
              {files.length > 0 && (
                <div className='audio-tools-upload__files audio-tools-upload__field--full-width'>
                  <h3 className='text-like-h4'>{copy.selectedFiles || 'Selected files'}</h3>
                  <ul>{files.map((file) => <li key={`${file.name}-${file.lastModified}`}>{file.name}</li>)}</ul>
                </div>
              )}
            </div>
          </div>
          <div className='panel__footer'>
            <div className='form-actions'>
              <button type='submit' className='button' disabled={busy}>{busy ? (copy.uploading || 'Uploading files …') : (copy.upload || 'Process batch securely')}</button>
            </div>
            {this.state.error && <p id='audio-tools-batch-error' className='panel panel--attention panel--compact audio-tools-upload__error' role='alert'>{this.state.error}</p>}
            {this.state.items.length > 0 && (
              <div className='audio-tools-upload__batch-results panel panel--compact' role='status' aria-live='polite'>
                <ul>
                  {this.state.items.map((item, index) => (
                    <li key={`${item.name}-${index}`}>
                      <strong>{item.name}</strong> — {this.statusLabel(item.status)}
                      {(item.status === 'uploading' || item.status === 'processing') && <progress max='100' value={item.progress} aria-label={`${copy.progress || 'Upload progress'}: ${item.name}`}>{item.progress}%</progress>}
                      {item.error && <p className='text-red' role='alert'>{item.error}</p>}
                      {item.status === 'ready' && item.job && item.job.downloadUrl && <a className='button button--ghost-secondary' href={item.job.downloadUrl}>{copy.download || 'Download result'}</a>}
                    </li>
                  ))}
                </ul>
                {(this.state.status === 'success' || this.state.status === 'partial') && <p>{this.state.status === 'success' ? (copy.batchComplete || 'The batch is complete.') : (copy.batchPartial || 'The batch is complete; some files could not be processed.')}</p>}
              </div>
            )}
          </div>
        </div>
      </form>
    );
  }
}

BatchLoudnessMatchingUpload.defaultProps = {
  batchLoudnessMatching: {
    maxFiles: 10,
    presets: [{ key: 'podcast_speech', label: 'Podcast & speech' }],
    copy: {}
  },
  acceptedFormats: []
};

document.querySelectorAll('[data-react-component=BatchLoudnessMatchingUpload]').forEach((element) => {
  try {
    ReactDOM.render(<BatchLoudnessMatchingUpload {...parseReactProps(element.dataset.reactProps)} />, element);
  } catch (error) {
    console.error('Failed to mount BatchLoudnessMatchingUpload', error);
  }
});

export default BatchLoudnessMatchingUpload;
